import React, { useMemo, useState } from 'react';
import { Alert, Button, Input, Table, Typography } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { CloseOutlined, InboxOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { SupplierOrderDto } from '../dto/supplierOrder';
import type { TransportSupplierOrderSummaryDto } from '../dto/transport';
import SupplierOrderProductsPreview from '../modules/orders/components/SupplierOrderProductsPreview';
import TransportSupplierOrderLines from './TransportSupplierOrderLines';
import SupplierOrderSummaryPreview, {
  getSupplierOrderSummary,
} from '../modules/orders/components/SupplierOrderSummaryPreview';
import './TransportSupplierOrdersField.css';

/**
 * The inbound half of "what is on this truck".
 *
 * The picker mirrors the supplier-order list: the operator sees the supplier,
 * canonical ZD number, status and every product before selecting a row. The
 * component owns only filtering and selection presentation; transport payload
 * state remains in SharedTransportForm.
 */
interface TransportSupplierOrdersFieldProps {
  availableSupplierOrders: SupplierOrderDto[];
  selectedSupplierOrders?: TransportSupplierOrderSummaryDto[];
  value: string[];
  onChange: (supplierOrderIds: string[]) => void;
  loading?: boolean;
  loadError?: boolean;
  onRetry?: () => void;
}

type SupplierOrderRow = SupplierOrderDto | TransportSupplierOrderSummaryDto;

// Candidates plus any persisted selection the candidate list no longer
// returns (e.g. a ZD that moved out of the pickable statuses) so an existing
// transport stays identifiable and removable.
function useSupplierOrderRows(
  availableSupplierOrders: SupplierOrderDto[],
  selectedSupplierOrders: TransportSupplierOrderSummaryDto[],
  value: string[],
): SupplierOrderRow[] {
  return useMemo<SupplierOrderRow[]>(() => {
    const rows = new Map<string, SupplierOrderRow>(
      availableSupplierOrders.map((supplierOrder) => [supplierOrder.id, supplierOrder]),
    );

    selectedSupplierOrders.forEach((supplierOrder) => {
      if (value.includes(supplierOrder.id) && !rows.has(supplierOrder.id)) {
        rows.set(supplierOrder.id, supplierOrder);
      }
    });

    return Array.from(rows.values());
  }, [availableSupplierOrders, selectedSupplierOrders, value]);
}

/**
 * Selected ZDs, rendered inline in the form section (outside the picker
 * modal) so the operator sees what is on the truck without opening anything.
 */
export const TransportSelectedSupplierOrdersList: React.FC<Pick<
  TransportSupplierOrdersFieldProps,
  'availableSupplierOrders' | 'selectedSupplierOrders' | 'value' | 'onChange'
> & {
  /** Owner 2026-10-10: the ZD's lines as this truck carries them (one card per ZD). */
  renderLines?: (supplierOrderId: string) => React.ReactNode;
}> = ({
  availableSupplierOrders,
  selectedSupplierOrders = [],
  value,
  onChange,
  renderLines,
}) => {
  const { t } = useTranslation(['transport', 'common']);
  const supplierOrderRows = useSupplierOrderRows(availableSupplierOrders, selectedSupplierOrders, value);
  const selectedRows = useMemo(
    () => supplierOrderRows.filter((supplierOrder) => value.includes(supplierOrder.id)),
    [supplierOrderRows, value],
  );

  if (selectedRows.length === 0) return null;

  return (
    <ul
      className="transport-supplier-orders__selected"
      data-testid="transport-supplier-orders-selected"
      aria-label={t('transport:supplierOrders.linkedTitle')}
    >
      {selectedRows.map((supplierOrder) => {
        const summary = getSupplierOrderSummary(supplierOrder);
        return (
          <li
            key={supplierOrder.id}
            className="transport-supplier-orders__selected-item"
            data-testid={`transport-supplier-orders-selected-${supplierOrder.id}`}
          >
            <span className="transport-supplier-orders__selected-tag">
              <InboxOutlined aria-hidden="true" /> {t('transport:supplierOrders.inboundTag')}
            </span>
            <div className="transport-supplier-orders__selected-main">
              <SupplierOrderSummaryPreview order={supplierOrder} />
              {renderLines ? renderLines(supplierOrder.id) : (
                <TransportSupplierOrderLines
                  items={'items' in supplierOrder ? supplierOrder.items : undefined}
                />
              )}
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              aria-label={t('transport:supplierOrders.remove', { number: summary.displayNumber })}
              onClick={() => onChange(value.filter((id) => id !== supplierOrder.id))}
            />
          </li>
        );
      })}
    </ul>
  );
};

const TransportSupplierOrdersField: React.FC<TransportSupplierOrdersFieldProps> = ({
  availableSupplierOrders,
  selectedSupplierOrders = [],
  value,
  onChange,
  loading = false,
  loadError = false,
  onRetry,
}) => {
  const { t } = useTranslation(['transport', 'common']);
  const [search, setSearch] = useState('');
  const supplierOrderRows = useSupplierOrderRows(availableSupplierOrders, selectedSupplierOrders, value);

  // Round 2 (U4): without a query the list shows the ZD not yet fully on
  // trucks (`truckCoverage`), plus the ones already chosen; typing searches all.
  const query = search.trim().toLocaleLowerCase('pl');

  const filteredSupplierOrders = useMemo(() => {
    if (!query) {
      return supplierOrderRows.filter((supplierOrder) => (
        value.includes(supplierOrder.id)
        || !('truckCoverage' in supplierOrder) || supplierOrder.truckCoverage !== 'FULL'
      ));
    }

    return supplierOrderRows.filter((supplierOrder) => {
      const summary = getSupplierOrderSummary(supplierOrder);
      const productText = ('items' in supplierOrder ? supplierOrder.items || [] : [])
        .map((item) => [item.name, item.description].filter(Boolean).join(' '))
        .join(' ');
      const searchText = [
        summary.displayNumber,
        supplierOrder.orderNumber,
        'manualOrderNumber' in supplierOrder ? supplierOrder.manualOrderNumber : undefined,
        summary.supplierName,
        'status' in supplierOrder ? supplierOrder.status : undefined,
        productText,
      ].filter(Boolean).join(' ').toLocaleLowerCase('pl');

      return searchText.includes(query);
    });
  }, [query, supplierOrderRows, value]);

  const columns = useMemo<ColumnsType<SupplierOrderDto | TransportSupplierOrderSummaryDto>>(
    () => [
      {
        title: t('transport:supplierOrders.orderColumn'),
        key: 'summary',
        width: '36%',
        onCell: () => ({
          'data-label': t('transport:supplierOrders.orderColumn'),
        } as React.TdHTMLAttributes<HTMLTableCellElement>),
        render: (_, supplierOrder) => (
          <SupplierOrderSummaryPreview order={supplierOrder} />
        ),
      },
      {
        title: t('transport:supplierOrders.productsColumn'),
        key: 'products',
        onCell: () => ({
          'data-label': t('transport:supplierOrders.productsColumn'),
        } as React.TdHTMLAttributes<HTMLTableCellElement>),
        render: (_, supplierOrder) => (
          <SupplierOrderProductsPreview
            items={'items' in supplierOrder ? supplierOrder.items : undefined}
          />
        ),
      },
    ],
    [t],
  );

  const rowSelection = useMemo<TableProps<SupplierOrderDto | TransportSupplierOrderSummaryDto>['rowSelection']>(() => ({
    selectedRowKeys: value,
    preserveSelectedRowKeys: true,
    columnWidth: 42,
    onChange: (keys) => onChange(keys.map(String)),
    getCheckboxProps: (supplierOrder) => {
      const summary = getSupplierOrderSummary(supplierOrder);
      return {
        name: `supplier-order-${supplierOrder.id}`,
        'aria-label': t('transport:supplierOrders.selectOrder', {
          number: summary.displayNumber,
        }),
      };
    },
    getTitleCheckboxProps: () => ({
      'aria-label': t('transport:supplierOrders.selectAll'),
    }),
  }), [onChange, t, value]);

  return (
    <section
      className="transport-supplier-orders"
      data-testid="transport-supplier-orders-field"
      aria-label={t('transport:supplierOrders.title')}
    >
      <Typography.Text type="secondary" className="transport-supplier-orders__hint">
        {t('transport:supplierOrders.hint')}
      </Typography.Text>

      {loadError ? (
        <Alert
          type="error"
          showIcon
          message={t('transport:errorFetchingOrders')}
          action={onRetry ? (
            <Button type="link" size="small" onClick={onRetry} disabled={loading}>
              {t('common:retry')}
            </Button>
          ) : null}
        />
      ) : null}

      <div className="transport-supplier-orders__toolbar">
        <Input
          allowClear
          className="transport-supplier-orders__search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          prefix={<SearchOutlined aria-hidden="true" />}
          placeholder={t('transport:supplierOrders.searchPlaceholder')}
          aria-label={t('transport:supplierOrders.searchPlaceholder')}
          data-testid="transport-supplier-orders-search"
        />
        <div className="transport-supplier-orders__selection-summary" aria-live="polite">
          <Typography.Text type={value.length > 0 ? undefined : 'secondary'}>
            {t('transport:supplierOrders.selectedCount', { count: value.length })}
          </Typography.Text>
          {value.length > 0 ? (
            <Button type="link" size="small" onClick={() => onChange([])}>
              {t('common:clear')}
            </Button>
          ) : null}
        </div>
      </div>

      {supplierOrderRows.length > 0 || loading ? (
        <Table<SupplierOrderDto | TransportSupplierOrderSummaryDto>
          className="transport-supplier-orders__table"
          data-testid="transport-supplier-orders-table"
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={filteredSupplierOrders}
          rowSelection={rowSelection}
          pagination={{ pageSize: 6, hideOnSinglePage: true }}
          locale={{ emptyText: t('transport:supplierOrders.empty') }}
          onRow={(supplierOrder) => ({
            onClick: (event) => {
              const target = event.target as HTMLElement;
              if (target.closest('button, a, input, label, .ant-checkbox-wrapper')) return;
              onChange(
                value.includes(supplierOrder.id)
                  ? value.filter((id) => id !== supplierOrder.id)
                  : [...value, supplierOrder.id],
              );
            },
          })}
        />
      ) : (
        <Typography.Text type="secondary" className="transport-supplier-orders__hint">
          {t('transport:supplierOrders.searchHint')}
        </Typography.Text>
      )}

    </section>
  );
};

export default TransportSupplierOrdersField;
